import * as K from 'common/constants';
import { routeState } from 'components/common/Managers/RouteStateManager.state';
import { settingsProcessedState } from 'components/common/Managers/SettingsManager.state';
import {
  accountDataReadyState,
  dashboardListState,
  dashboardsAccessibleState,
  featurePermissionsReadyState,
  isManageGroupsEnabledState,
  searchAccessibleState,
} from 'components/account/account.state';
import { recoilSelector } from 'common/util/recoil-selector';

/**
 * We need to wait until some necessary state has been fetched from the server before we can safely render the Router
 * and its children
 */
export const isRouterReadyState = recoilSelector<boolean>({
  key: K.RECOIL_SELECTOR_KEY.IS_ROUTER_READY,
  get: ({ get }) => get(accountDataReadyState) && get(featurePermissionsReadyState) && get(settingsProcessedState),
});

/**
 * Returns true if the current route is enabled for the current user based on the user's roles, permissions, and labs
 */
export const isRouteEnabledState = recoilSelector<boolean>({
  key: K.RECOIL_SELECTOR_KEY.IS_ROUTE_ENABLED,
  get: ({ get }) => {
    const route = get(routeState);
    const searchAccessible = get(searchAccessibleState);
    const dashboardsAccessible = get(dashboardsAccessibleState);
    const isManageGroupsEnabled = get(isManageGroupsEnabledState);
    if (!searchAccessible && [K.ROUTE.EVENTS].includes(route)) {
      return false;
    }
    if (!dashboardsAccessible && [K.ROUTE.DASHBOARD, K.ROUTE.DASHBOARD_LIST].includes(route)) {
      return false;
    }
    if (!isManageGroupsEnabled && K.ROUTE.MANAGE_GROUPS === route) {
      return false;
    }
    return true;
  },
});

/**
 * Returns the home page url, which varies for different user permissions and roles
 */
export const homePageState = recoilSelector({
  key: K.RECOIL_SELECTOR_KEY.HOME_PAGE,
  get: ({ get }) => {
    const searchAccessible = get(searchAccessibleState);
    const dashboardsAccessible = get(dashboardsAccessibleState);
    const dashboardList = get(dashboardListState);

    if (searchAccessible) {
      return K.ROUTE.EVENTS;
    }

    const firstDashboardName = dashboardList[0]?.name;
    if (dashboardsAccessible && firstDashboardName) {
      return `${K.ROUTE.DASHBOARD}?${K.URL_PARAM.DASHBOARD_NAME}=${firstDashboardName}`;
    }

    // return access denied message if the user doesn't have access to dashboard or search
    return K.ACCESS_DENIED;
  },
});
